import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://blog.bonlineco.com',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Redirect to login if not already there
      if (window.location.pathname !== '/admin_v3jq5z') {
        window.location.href = '/admin_v3jq5z';
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const apiService = {
  // Health check
  health: () => api.get('/api/health'),
  
  // Get authenticated user
  getUser: () => api.get('/api/user'),
  
  // Authentication endpoints
  login: (credentials) => api.post('/api/auth/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  logout: () => api.post('/api/auth/logout'),
  validateToken: () => api.get('/api/auth/validate-token'),
  updateProfile: (data) => api.put('/api/profile', data),
  uploadAvatar: (formData) => api.post('/api/profile/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // CSRF Cookie (required for Sanctum)
  getCsrfCookie: () => api.get('/sanctum/csrf-cookie'),

  // Posts endpoints
  getPosts: (params) => api.get('/api/posts', { params }),
  getPost: (id) => api.get(`/api/posts/${id}`),
  createPost: (data) => api.post('/api/posts', data),
  updatePost: (id, data) => api.put(`/api/posts/${id}`, data),
  deletePost: (id) => api.delete(`/api/posts/${id}`),
  generatePostSlug: (title) => api.post('/api/posts/generate-slug', { title }),
  incrementPostViews: (id) => api.post(`/api/posts/${id}/view`),

  // Categories endpoints
  getCategories: (params) => api.get('/api/categories', { params }),
  getCategory: (id) => api.get(`/api/categories/${id}`),
  createCategory: (data) => api.post('/api/categories', data),
  updateCategory: (id, data) => api.put(`/api/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/categories/${id}`),
  generateCategorySlug: (name) => api.post('/api/categories/generate-slug', { name }),

  // Media endpoints
  getMedia: () => api.get('/api/media'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/api/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteImage: (path) => api.delete('/api/media/delete', { data: { path } }),

  // Users endpoints
  getUsers: (params) => api.get('/api/users', { params }),
  getUserById: (id) => api.get(`/api/users/${id}`),
  createUser: (data) => api.post('/api/users', data),
  updateUser: (id, data) => api.put(`/api/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/users/${id}`),
  uploadUserAvatar: (userId, formData) => api.post(`/api/users/${userId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Dashboard endpoints
  getDashboardStats: () => api.get('/api/dashboard/stats'),

  // Site statistics (public)
  getSiteStats: () => api.get('/api/site-stats'),
  incrementSiteVisit: () => api.post('/api/site-visit'),

  // Settings endpoints
  getSettings: () => api.get('/api/settings'),
  updateSettings: (data) => api.post('/api/settings/batch', data),
  generateSitemap: () => api.post('/api/settings/generate-sitemap'),
  getPublicSettings: () => api.get('/api/public-settings'),

  // Contact endpoints
  submitContact: (data) => api.post('/api/contact', data),
  getContacts: (params) => api.get('/api/contacts', { params }),
  getContact: (id) => api.get(`/api/contacts/${id}`),
  updateContact: (id, data) => api.put(`/api/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/api/contacts/${id}`),
};

export default api;
